import React, { useEffect, useState } from "react";
import {
  GoogleMap,
  Marker,
  OverlayView,
  DirectionsRenderer,
  useLoadScript,
} from "@react-google-maps/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWalking } from "@fortawesome/free-solid-svg-icons";
import { GoogleMapStyle, WALK_STATUS } from "../../../config/constants";
import {
  PickupLocationIcon,
  WalkerLocationIcon,
  WalkInProgressIcon,
} from "../../../config/googlemapstyle";
import StatusMessage from "./StatusMessage";

const mapContainerStyle = {
  width: "100%",
  height: "50vh",
  maxHeight: "600px",
  borderRadius: "8px", // Adds rounded corners
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)", // Soft shadow for a polished effect
};

const WalkMapStatus = ({ walk }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const [status, setStatus] = useState(walk.status);
  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [mapData, setMapData] = useState({});

  const handleStatusFlow = () => {
    switch (status) {
      case WALK_STATUS.REQUESTED:
        return "Sit tight! We're finding the best walker for your pet.";
      case WALK_STATUS.FINDING_WALKER:
        return "Hang on, we’re on the lookout for a walker!";
      case WALK_STATUS.WALKER_ASSIGNED:
        return "Great news! A walker is assigned and heading to your location.";
      case WALK_STATUS.HEADING_TO_PICKUP:
        return "Your walker is on the way to the pickup location!";
      case WALK_STATUS.PICKED_UP:
        return "Your walker has arrived and is ready to meet your pet.";
      case WALK_STATUS.IN_PROGRESS:
        return "Your pet is enjoying the walk! 🐾";
      case WALK_STATUS.COMPLETED:
        return "Walk completed! Your pet is safely back home.";
      case WALK_STATUS.CANCELLED:
        return "This walk was cancelled. Please check details.";
      default:
        return "Status unknown. Please check back later.";
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (
        status == WALK_STATUS.REQUESTED ||
        status == WALK_STATUS.FINDING_WALKER
      ) {
        setMapData((prev) => ({
          ...prev,
          pickup_latitude: walk.pickup_location.latitude,
          pickup_longitude: walk.pickup_location.longitude,
          pickup_address: walk.pickup_location.address,
        }));
      }

      if (
        status == WALK_STATUS.WALKER_ASSIGNED ||
        status == WALK_STATUS.HEADING_TO_PICKUP ||
        status == WALK_STATUS.PICKED_UP
      ) {
        if (walk && walk.pickup_location) {
          setMapData((prev) => ({
            ...prev,
            pickup_latitude: walk.pickup_location.latitude,
            pickup_longitude: walk.pickup_location.longitude,
            pickup_address: walk.pickup_location.address,
          }));
        }

        if (walk && walk.walker.pin_location) {
          setMapData((prev) => ({
            ...prev,
            walker_latitude: walk.walker.pin_location.latitude,
            walker_longitude: walk.walker.pin_location.longitude,
            walker_address: walk.walker.pin_location.address,
          }));
        }
      }

      if (status == WALK_STATUS.IN_PROGRESS) {
        if (walk && walk.walker.pin_location) {
          setMapData((prev) => ({
            ...prev,
            walker_latitude: walk.walker.pin_location.latitude,
            walker_longitude: walk.walker.pin_location.longitude,
            walker_address: walk.walker.pin_location.address,
          }));
        }
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, []);

  // Second useEffect to trigger calculateRoute after mapData is fully set
  useEffect(() => {
    if (
      mapData.pickup_latitude &&
      mapData.walker_latitude &&
      (status === WALK_STATUS.HEADING_TO_PICKUP ||
        status === WALK_STATUS.PICKED_UP)
    ) {
      const routeTimeoutId = setTimeout(() => {
        calculateRoute();
      }, 1000);
      return () => clearTimeout(routeTimeoutId);
    }
  }, [mapData]);

  const calculateRoute = () => {
    if (!mapData.pickup_latitude || !mapData.walker_latitude) return;

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: {
          lat: mapData.walker_latitude,
          lng: mapData.walker_longitude,
        },
        destination: {
          lat: mapData.pickup_latitude,
          lng: mapData.pickup_longitude,
        },
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirectionsResponse(result);
          setDistance(result.routes[0].legs[0].distance.text);
          setDuration(result.routes[0].legs[0].duration.text);
        } else {
          // Error fetching directions
        }
      }
    );
  };

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading Maps...</div>;

  return (
    <div className="walk-map-status">
      <StatusMessage status={status} message={handleStatusFlow()} />
      <div
        className={` ${status === WALK_STATUS.COMPLETED ? "hidden" : "block"}`}
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={{
            lat: walk.pickup_location.latitude,
            lng: walk.pickup_location.longitude,
          }}
          zoom={14}
          options={{
            styles: GoogleMapStyle,
            disableDefaultUI: true,
            zoomControl: true,
          }}
        >
          {(status == WALK_STATUS.FINDING_WALKER ||
            status == WALK_STATUS.REQUESTED) && (
              <>
                {mapData.pickup_latitude && (
                  <>
                    <Marker
                      position={{
                        lat: mapData.pickup_latitude,
                        lng: mapData.pickup_longitude,
                      }}
                      icon={{
                        url: PickupLocationIcon,
                        scaledSize: new window.google.maps.Size(56, 76),
                      }}
                      animation={"BOUNCE"}
                    />

                    <OverlayView
                      position={{
                        lat: mapData.pickup_latitude,
                        lng: mapData.pickup_longitude,
                      }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div className="relative flex items-center justify-center">
                        <div className="absolute w-36 h-36 rounded-full bg-primary opacity-50 animate-pulse-circle-delay-2"></div>
                      </div>
                    </OverlayView>
                  </>
                )}
              </>
            )}

          {status == WALK_STATUS.WALKER_ASSIGNED && (
            <>
              {mapData.pickup_latitude && (
                <Marker
                  position={{
                    lat: mapData.pickup_latitude,
                    lng: mapData.pickup_longitude,
                  }}
                  icon={{
                    url: PickupLocationIcon,
                    scaledSize: new window.google.maps.Size(56, 76),
                  }}
                />
              )}
              {mapData.walker_latitude && (
                <Marker
                  position={{
                    lat: mapData.walker_latitude,
                    lng: mapData.walker_longitude,
                  }}
                  icon={{
                    url: WalkerLocationIcon,
                    scaledSize: new window.google.maps.Size(36, 46), // Ensure the icon is scaled to the right size
                  }}
                />
              )}
            </>
          )}

          {(status == WALK_STATUS.HEADING_TO_PICKUP ||
            status == WALK_STATUS.PICKED_UP) && (
              <>
                {mapData.pickup_latitude && (
                  <Marker
                    position={{
                      lat: mapData.pickup_latitude,
                      lng: mapData.pickup_longitude,
                    }}
                    icon={{
                      url: PickupLocationIcon,
                      scaledSize: new window.google.maps.Size(56, 76),
                    }}
                  />
                )}
                {mapData.walker_latitude && (
                  <Marker
                    position={{
                      lat: mapData.walker_latitude,
                      lng: mapData.walker_longitude,
                    }}
                    icon={{
                      url: WalkerLocationIcon,
                      scaledSize: new window.google.maps.Size(36, 46), // Ensure the icon is scaled to the right size
                    }}
                  />
                )}
                {directionsResponse && (
                  <DirectionsRenderer
                    directions={directionsResponse}
                    options={{
                      suppressMarkers: true,
                      polylineOptions: {
                        strokeColor: "#9A48FF",
                        strokeOpacity: 1,
                        strokeWeight: 5,
                      },
                    }}
                  />
                )}
              </>
            )}

          {status == WALK_STATUS.IN_PROGRESS && (
            <>
              {mapData.walker_latitude && (
                <>
                  <Marker
                    position={{
                      lat: mapData.walker_latitude,
                      lng: mapData.walker_longitude,
                    }}
                    icon={{
                      url: WalkInProgressIcon,
                      scaledSize: new window.google.maps.Size(50, 50),
                    }}
                    animation={"BOUNCE"}
                  />

                  <OverlayView
                    position={{
                      lat: mapData.walker_latitude,
                      lng: mapData.walker_longitude,
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <div className="relative flex items-center justify-center">
                      <div className="absolute w-16 h-16 rounded-full bg-primary opacity-30 animate-pulse-circle-delay-1"></div>
                    </div>
                  </OverlayView>
                </>
              )}
            </>
          )}
        </GoogleMap>
      </div>

      {status === WALK_STATUS.HEADING_TO_PICKUP ? (
        <div className="mt-4 text-center text-gray-700">
          <FontAwesomeIcon icon={faWalking} className="mr-2 text-purple-600" />
          Walker is approximately {<span className="font-bold text-green-400">{distance}</span> || "calculating..."} away and will
          arrive in approximately {<span className="font-bold text-primary">{duration}</span> || "calculating..."}{" "}
        </div>
      ) : null}
    </div>
  );
};

export default WalkMapStatus;
